import { Logger } from '@gitbook/util-debug';

import type { Block, ChildNode, Fragment, Operation, Text, Value } from '../models';
import { BlockRange } from '../models/block-range';
import { TextRange } from '../models/text-range';

const logger = Logger('slate:operation:apply');

/**
 * Apply adjustments to affected ranges (selections);
 * accepts (value, checking function(range) -> bool, applying function(range) -> range)
 * returns value with affected ranges updated
 */
function applyRangeAdjustments(
    value: Value,
    checkAffected: (range: TextRange) => boolean,
    adjustRange: (range: TextRange) => TextRange
) {
    // check selection, apply adjustment if affected. Selection adjustments here
    // operate on the text-range shape of the selection.
    const selection = value.selection as TextRange;
    if (selection && checkAffected(selection)) {
        value = value.set('selection', adjustRange(selection));
    }

    return value;
}

/**
 * Apply adjustments to selections for a node to be removed.
 */
function applyRemoveNodeRangeAdjustments(value: Value, node: ChildNode | Fragment) {
    return applyRangeAdjustments(
        value,
        // If the start or end point was in this node
        ({ startKey, endKey }) => node.hasNode(startKey) || node.hasNode(endKey),
        // update it to be just before/after
        (range) => {
            const { startKey, endKey } = range;

            const prev = value.document.getPreviousTextBeforeNode(node.key);
            const next = value.document.getNextTextAfterNode(node.key);

            if (node.hasNode(startKey)) {
                range = prev
                    ? range.moveStartTo(prev.key, prev.text.length)
                    : next
                      ? range.moveStartTo(next.key, 0)
                      : range.deselect();
            }

            if (node.hasNode(endKey)) {
                range = prev
                    ? range.moveEndTo(prev.key, prev.text.length)
                    : next
                      ? range.moveEndTo(next.key, 0)
                      : range.deselect();
            }

            // If the range wasn't deselected, normalize it.
            if (range.isSet) {
                return range.normalize(value.document);
            }

            return range;
        }
    );
}

/*
 * clear any atomic ranges if they contain the point (key, offset, offset-end?)
 * specified
 *
 * @param {Value} value
 * @param {String} key
 * @param {Number} offset
 * @param {Number?} offsetEnd
 * @return {Value}
 */

function clearAtomicRangesIfContains(
    value: Value,
    key: string,
    offset: number,
    offsetEnd: number | null = null
) {
    return applyRangeAdjustments(
        value,
        (range) => {
            if (!range.isAtomic) {
                return false;
            }
            const { startKey, startOffset, endKey, endOffset } = range;
            return Boolean(
                (startKey === key &&
                    startOffset < offset &&
                    (endKey !== key || endOffset > offset)) ||
                (offsetEnd &&
                    startKey === key &&
                    startOffset < offsetEnd &&
                    (endKey !== key || endOffset > offsetEnd))
            );
        },
        (range) => range.deselect()
    );
}

/*
 * Applying functions.
 *
 * @type {Object}
 */

const APPLIERS = {
    //
    // Nodes
    //

    /*
     * Insert a `node` at `index` in a node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    insert_node(value: Value, operation: Operation) {
        const { path } = operation;
        const node = operation.node as ChildNode;
        const index = path[path.length - 1];
        const rest = path.slice(0, -1);
        let { document } = value;

        // If there are conflicts with keys and a risk to generate duplicated keys; we skip
        if (document.hasNodeKeyConflict(node)) {
            logger.error(`skip insert_node, node "${node.key}" is conflicting with existing keys`);
            return value;
        }

        let parent = document.assertPath(rest) as Block;
        parent = parent.insertNode(index, node);
        document = document.updateNode(parent);
        value = value.set('document', document);
        return value;
    },

    /*
     * Remove a node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    remove_node(value: Value, operation: Operation) {
        const { path } = operation;
        let { document } = value;
        const { selection } = value;
        const node = document.assertPath(path) as ChildNode;

        if (selection.isSet) {
            value = applyRemoveNodeRangeAdjustments(value, node);
        }

        // Remove the node from the document.
        let parent = document.getParent(node.key)!;
        const index = parent.getChildIndex(node.key);
        parent = parent.removeNode(index);
        document = document.updateNode(parent);

        // Update the document and range.
        value = value.set('document', document);
        return value;
    },

    /*
     * Move a node by `path` to `newPath`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    move_node(value: Value, operation: Operation) {
        const { path, newPath } = operation;
        const newIndex = newPath[newPath.length - 1];
        const newParentPath = newPath.slice(0, -1);
        const oldParentPath = path.slice(0, -1);
        const oldIndex = path[path.length - 1];
        let { document } = value;
        const node = document.assertPath(path);

        // Remove the node from its current parent.
        let parent = document.getParent(node.key)!;
        parent = parent.removeNode(oldIndex);
        document = document.updateNode(parent);

        // Find the new target...
        let target;

        // If the old path and the rest of the new path are the same, then the new
        // target is the old parent.
        if (
            oldParentPath.every((x, i) => x === newParentPath[i]) &&
            oldParentPath.length === newParentPath.length
        ) {
            target = parent;
        } else if (
            oldParentPath.every((x, i) => x === newParentPath[i]) &&
            oldIndex < newParentPath[oldParentPath.length]
        ) {
            // Otherwise, if the old path removal resulted in the new path being no longer
            // correct, we need to decrement the new path at the old path's last index.
            newParentPath[oldParentPath.length]--;
            target = document.assertPath(newParentPath);
        } else {
            // Otherwise, we can just grab the target normally...
            target = document.assertPath(newParentPath);
        }

        // Insert the new node to its new parent.
        target = target.insertNode(newIndex, node);
        document = document.updateNode(target);
        value = value.set('document', document);
        return value;
    },

    /*
     * Merge a node at `path` with the previous node.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    merge_node(value: Value, operation: Operation) {
        const { path } = operation;
        const withPath = path.slice(0, path.length - 1).concat([path[path.length - 1] - 1]);
        let { document } = value;
        const one = document.assertPath(withPath);
        const two = document.assertPath(path);
        let parent = document.getParent(one.key)!;
        const oneIndex = parent.getChildIndex(one.key);
        const twoIndex = parent.getChildIndex(two.key);

        // Perform the merge in the document.
        parent = parent.mergeNode(oneIndex, twoIndex);
        document = document.updateNode(parent);
        value = value.set('document', document);

        if (one.object === 'text') {
            value = applyRangeAdjustments(
                value,
                // If the nodes are text nodes and the range is inside the second node:
                ({ anchorKey, focusKey }) => anchorKey === two.key || focusKey === two.key,
                // update it to refer to the first node instead:
                (range) => {
                    if (range.anchorKey === two.key) {
                        range = range.moveAnchorTo(one.key, one.text.length + range.anchorOffset);
                    }
                    if (range.focusKey === two.key) {
                        range = range.moveFocusTo(one.key, one.text.length + range.focusOffset);
                    }
                    return range.normalize(document);
                }
            );
        }

        return value;
    },

    /*
     * Split a node by `path` at `offset`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    split_node(value: Value, operation: Operation) {
        const { path, position } = operation;
        const { key, ...properties } = operation.properties as {
            key?: string;
            [prop: string]: unknown;
        };
        let { document } = value;

        // Calculate a few things...
        const node = document.assertPath(path);
        let parent = document.getParent(node.key)!;
        const index = parent.getChildIndex(node.key);

        // Split the node by its parent.
        parent = parent.splitNode(index, position, key);

        if (properties) {
            const splitNode = parent.nodes.get(index + 1)!;

            if (splitNode.object !== 'text') {
                parent = parent.updateNode(splitNode.merge(properties));
            }
        }

        document = document.updateNode(parent);
        const next = document.getNextText(node.key)!;

        value = applyRangeAdjustments(
            value,
            // check if range is affected
            ({ startKey, startOffset, endKey, endOffset }) =>
                (node.key === startKey && position <= startOffset) ||
                (node.key === endKey && position <= endOffset),
            // update its start / end as needed
            (range) => {
                const { startKey, startOffset, endKey, endOffset } = range;
                let normalize = false;

                if (node.key === startKey && position <= startOffset) {
                    range = range.moveStartTo(next.key, startOffset - position);
                    normalize = true;
                }

                if (node.key === endKey && position <= endOffset) {
                    range = range.moveEndTo(next.key, endOffset - position);
                    normalize = true;
                }

                // Normalize the selection if we changed it
                if (normalize) {
                    return range.normalize(document);
                }
                return range;
            }
        );

        // Return the updated value.
        value = value.set('document', document);
        return value;
    },

    /*
     * Set `properties` on a node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    set_node(value: Value, operation: Operation) {
        const { path, properties } = operation;
        let { document } = value;
        let node = document.assertPath(path);
        // properties is a loose node-create-props bag; the concrete node's merge
        // accepts its own Partial shape.
        node = node.merge(properties as Parameters<typeof node.merge>[0]);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    //
    // Fragments
    //

    /**
     * Set a `fragment` on a node.
     */
    set_fragment(value: Value, operation: Operation) {
        const { path } = operation;
        const node = operation.node as Fragment;
        let { document } = value;
        let parent = document.assertPath(path) as Block;
        parent = parent.setFragment(node);
        document = document.updateNode(parent);
        value = value.set('document', document);
        return value;
    },

    /**
     * Insert a `fragment` on a node.
     */
    insert_fragment(value: Value, operation: Operation) {
        const { path, fragmentIndex } = operation;
        const node = operation.node as Fragment;
        let { document } = value;
        let parent = document.assertPath(path) as Block;
        parent = parent.insertFragment(node, fragmentIndex);
        document = document.updateNode(parent);
        value = value.set('document', document);
        return value;
    },

    /*
     * Remove a `fragment` from a node.
     */
    remove_fragment(value: Value, operation: Operation) {
        const { path } = operation;
        const node = operation.node as Fragment;
        let { document, selection } = value;
        let parent = document.assertPath(path) as Block;
        const fragmentNode = parent.getFragmentByKey(node.fragmentKey);

        if (selection.isSet) {
            value = applyRemoveNodeRangeAdjustments(value, fragmentNode!);
        }

        parent = parent.removeFragmentByKey(node.fragmentKey);
        document = document.updateNode(parent);
        value = value.set('document', document);
        return value;
    },

    //
    // Text
    //

    /*
     * Insert `text` at `offset` in node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    insert_text(value: Value, operation: Operation) {
        const { path, offset, text, marks } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Text;

        // Update the document
        node = node.insertText(offset, text, marks);
        document = document.updateNode(node);

        value = value.set('document', document);

        // if insert happens within atomic ranges, clear
        value = clearAtomicRangesIfContains(value, node.key, offset);

        // Update the selection
        value = applyRangeAdjustments(
            value,
            ({ anchorKey, anchorOffset, isBackward, isAtomic }) =>
                anchorKey === node.key &&
                (anchorOffset > offset || (anchorOffset === offset && (!isAtomic || !isBackward))),
            (range) => range.moveAnchor(text.length)
        );

        value = applyRangeAdjustments(
            value,
            ({ focusKey, focusOffset, isBackward, isAtomic }) =>
                Boolean(
                    focusKey === node.key &&
                    (focusOffset > offset || (focusOffset === offset && (!isAtomic || isBackward)))
                ),
            (range) => range.moveFocus(text.length)
        );

        return value;
    },

    /*
     * Remove `text` at `offset` in node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    remove_text(value: Value, operation: Operation) {
        const { path, offset, text } = operation;
        const { length } = text;
        const rangeOffset = offset + length;
        let { document } = value;

        let node = document.assertPath(path);

        // if insert happens within atomic ranges, clear
        value = clearAtomicRangesIfContains(value, node.key, offset, offset + length);

        value = applyRangeAdjustments(
            value,
            // if anchor of range is here
            ({ anchorKey }) => anchorKey === node.key,
            // adjust if it is in or past the removal range
            (range) =>
                range.anchorOffset >= rangeOffset
                    ? range.moveAnchor(-length)
                    : range.anchorOffset > offset
                      ? range.moveAnchorTo(range.anchorKey!, offset)
                      : range
        );

        value = applyRangeAdjustments(
            value,
            // if focus of range is here
            ({ focusKey }) => focusKey === node.key,
            // adjust if it is in or past the removal range
            (range) =>
                range.focusOffset >= rangeOffset
                    ? range.moveFocus(-length)
                    : range.focusOffset > offset
                      ? range.moveFocusTo(range.focusKey!, offset)
                      : range
        );

        node = (node as Text).removeText(offset, length);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    //
    // Marks
    //

    /*
     * Add mark to text at `offset` and `length` in node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    add_mark(value: Value, operation: Operation) {
        const { path, offset, length, mark } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Text;
        node = node.addMark(offset, length, mark);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    /*
     * Remove mark from text at `offset` and `length` in node by `path`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    remove_mark(value: Value, operation: Operation) {
        const { path, offset, length, mark } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Text;
        node = node.removeMark(offset, length, mark);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    //
    // Selection
    //

    /*
     * Set `properties` on the selection.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    set_selection(value: Value, operation: Operation) {
        const properties = operation.properties as {
            anchorPath?: number[] | null;
            focusPath?: number[] | null;
            anchorBlock?: string | null;
            focusBlock?: string | null;
            [prop: string]: unknown;
        };
        const { anchorPath, focusPath, anchorBlock, focusBlock, ...props } = properties;
        let { selection } = value;
        const { document } = value;

        const isBlock = anchorBlock || focusBlock;
        const isText =
            anchorPath ||
            focusPath ||
            props.anchorKey ||
            props.focusKey ||
            props.anchorOffset ||
            props.focusOffset;

        if (isText) {
            if (anchorPath !== undefined) {
                props.anchorKey = anchorPath == null ? null : document.assertPath(anchorPath).key;
            }

            if (focusPath !== undefined) {
                props.focusKey = focusPath == null ? null : document.assertPath(focusPath).key;
            }

            // Retain the focus from current selection if switching modes
            props.isFocused ??= selection.isFocused;

            selection = TextRange.isTextRange(selection)
                ? selection.merge(props)
                : TextRange.create(props);
        } else if (isBlock) {
            if (anchorBlock !== undefined) {
                props.anchorBlock = anchorBlock == null ? null : anchorBlock;
            }
            if (focusBlock !== undefined) {
                props.focusBlock = focusBlock == null ? null : focusBlock;
            }

            // Retain the focus from current selection if switching modes
            props.isFocused ??= selection.isFocused;

            selection = BlockRange.isBlockRange(selection)
                ? selection.merge(props)
                : BlockRange.create(props);
        } else {
            // merge props with existing selection as we can't determine the type
            selection = selection.merge(props);
        }
        selection = selection.normalize(document);
        value = value.set('selection', selection);
        return value;
    },

    //
    // Value
    //

    /*
     * Set `properties` on `value`.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    set_value(value: Value, operation: Operation) {
        const { properties } = operation;
        value = value.merge(properties as Parameters<typeof value.merge>[0]);
        return value;
    },

    /**
     * Set data in a node.
     */
    set_data(value: Value, operation: Operation) {
        const { path, data, dataPath } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Block;
        node = node.setData(dataPath, data);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    /*
     * Insert an element into an array on the data of a node.
     */
    insert_array_data(value: Value, operation: Operation) {
        const { data, dataPath, path, dataIndex } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Block;
        node = node.insertArrayData(dataPath, data, dataIndex);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    /*
     * Remove an element from an array on the data of a node.
     */
    remove_array_data(value: Value, operation: Operation) {
        const { data, dataPath, path } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Block;
        node = node.removeArrayData(dataPath, data);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    /*
     * Remove data from the node.
     */
    remove_data(value: Value, operation: Operation) {
        const { path, dataPath } = operation;
        let { document } = value;
        let node = document.assertPath(path) as Block;
        node = node.removeData(dataPath);
        document = document.updateNode(node);
        value = value.set('document', document);
        return value;
    },

    //
    // Dev Utils
    //

    /*
     * Set the document value.
     *
     * @param {Value} value
     * @param {Operation} operation
     * @return {Value}
     */
    set_document(value: Value, operation: Operation) {
        const { data } = operation;
        value = value.set('document', data).set('selection', value.selection.deselect());
        return value;
    },
};

/*
 * Apply an `operation` to a `value`.
 */
export function applyOperation(value: Value, operation: Operation) {
    const { type } = operation;
    const apply = APPLIERS[type];

    if (!apply) {
        throw new Error(`Unknown operation type: "${type}".`);
    }
    value = apply(value, operation);
    return value;
}
